import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { submitPostprocessingToken } from '../../../Repository/Order/OrderRepository.js'
import { LoadingIndicator, Paragraph } from '../../Common/index.js'

type Status =
  | 'LOADING'
  | 'SUCCESS'
  | 'INVALID_TOKEN'
  | 'EXPIRED_TOKEN'
  | 'ALREADY_USED_TOKEN'
  | 'ORDER_STATE_CONFLICT'
  | 'PAYMENT_REQUIRED'
  | 'INTERMEDIATES_EXPIRED'
  | 'GENERIC_ERROR'

export const ConfirmPostprocessingPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [status, setStatus] = useState<Status>('LOADING')
  const [orderId, setOrderId] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setStatus('INVALID_TOKEN')
      return
    }

    submitPostprocessingToken(token).subscribe({
      next: (response) => {
        setStatus(response.data.status as Status)
        setOrderId(response.data.orderId)
      },
      error: (err) => {
        console.error('Failed to submit postprocessing token', err)
        setStatus('GENERIC_ERROR')
      }
    })
  }, [token])

  const renderContent = () => {
    switch (status) {
      case 'LOADING':
        return (
          <div className="flex flex-col items-center gap-4">
            <LoadingIndicator />
            <Paragraph>Confirming postprocessing request...</Paragraph>
          </div>
        )
      case 'SUCCESS':
        return (
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold">Postprocessing Started!</h2>
            <Paragraph>Your request has been processed successfully. The postprocessing has been started.</Paragraph>
            {orderId && (
              <Link to={`/order/${orderId}`} className="btn btn-primary mt-4">
                View Order Details
              </Link>
            )}
          </div>
        )
      case 'INVALID_TOKEN':
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Invalid Token</h2>
            <Paragraph>The confirmation link you used is invalid. Please make sure you copied the full link from your email.</Paragraph>
          </div>
        )
      case 'EXPIRED_TOKEN':
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-yellow-600">Link Expired</h2>
            <Paragraph>This confirmation link has expired. Links are usually valid for 16 hours.</Paragraph>
          </div>
        )
      case 'ALREADY_USED_TOKEN':
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-blue-600">Already Confirmed</h2>
            <Paragraph>This request has already been confirmed. No further action is needed.</Paragraph>
            {orderId && (
              <Link to={`/order/${orderId}`} className="btn btn-primary mt-4">
                View Order Details
              </Link>
            )}
          </div>
        )
      case 'ORDER_STATE_CONFLICT':
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Cannot Start Postprocessing</h2>
            <Paragraph>The order is not in a state where postprocessing can be started, or it has already been started.</Paragraph>
            {orderId && (
              <Link to={`/order/${orderId}`} className="btn btn-primary mt-4">
                View Order Details
              </Link>
            )}
          </div>
        )
      case 'PAYMENT_REQUIRED':
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Insufficient Credits</h2>
            <Paragraph>
              Your organization does not have enough credits to start postprocessing for this order. Please recharge your credits and try
              again.
            </Paragraph>
            {orderId && (
              <Link to={`/order/${orderId}`} className="btn btn-primary mt-4">
                View Order Details
              </Link>
            )}
          </div>
        )
      case 'INTERMEDIATES_EXPIRED':
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Intermediates Expired</h2>
            <Paragraph>
              The preprocessing results this order needs for postprocessing have been deleted, so postprocessing cannot start. The
              processing lab has to re-run preprocessing first. No credits have been charged.
            </Paragraph>
            {orderId && (
              <Link to={`/order/${orderId}`} className="btn btn-primary mt-4">
                View Order Details
              </Link>
            )}
          </div>
        )
      case 'GENERIC_ERROR':
      default:
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">An Error Occurred</h2>
            <Paragraph>Something went wrong while processing your request. Please try again later or contact support.</Paragraph>
          </div>
        )
    }
  }

  return <div className="max-w-md w-full mx-auto mt-20 p-8 bg-white rounded-xl shadow-lg border border-gray-100">{renderContent()}</div>
}
